<template>
  <div class="page-wrapper">
    <main-navigation></main-navigation>

    <div class="add-unit-container">

      <!-- LEFT INFO SECTION -->
      <div class="info-section">
        <div class="info-card">
          <div class="info-header">
            <div class="icon-wrapper">
              <i class="bi bi-cloud-arrow-up-fill"></i>
            </div>
            <h2 class="title">Bulk Upload Units</h2>
            <p class="subtitle">Quickly add multiple units using CSV template</p>
          </div>

          <div class="template-card">
            <div class="template-header">
              <i class="bi bi-file-earmark-spreadsheet"></i>
              <h5>Step 1: Download Template</h5>
            </div>
            <p class="template-desc">Get our pre-formatted CSV file with the correct columns</p>
            <button class="btn-template-download" @click="downloadTemplate">
              <i class="bi bi-download"></i>
              Download CSV Template
            </button>
            <div class="template-features">
              <div class="feature">
                <i class="bi bi-check-circle-fill"></i>
                <span>Pre-formatted columns</span>
              </div>
              <div class="feature">
                <i class="bi bi-check-circle-fill"></i>
                <span>Sample data included</span>
              </div>
              <div class="feature">
                <i class="bi bi-check-circle-fill"></i>
                <span>Error prevention</span>
              </div>
            </div>
          </div>

          <div class="upload-process">
            <h5>Step 2: Fill & Upload</h5>
            <div class="process-steps">
              <div class="step">
                <div class="step-number">1</div>
                <div class="step-content">
                  <strong>Edit the template</strong>
                  <p>Fill in unit details for your property</p>
                </div>
              </div>
              <div class="step">
                <div class="step-number">2</div>
                <div class="step-content">
                  <strong>Upload CSV file</strong>
                  <p>Select your completed file</p>
                </div>
              </div>
              <div class="step">
                <div class="step-number">3</div>
                <div class="step-content">
                  <strong>Review & Confirm</strong>
                  <p>Check upload results</p>
                </div>
              </div>
            </div>
          </div>

          <!-- UPLOAD AREA -->
          <div class="upload-section">
            <div class="upload-card">
              <h5 class="upload-title">Ready to Upload</h5>
              
              <div class="house-select-wrapper">
                <label class="form-label">
                  <i class="bi bi-building"></i> Select Property
                </label>
                <div class="select-with-icon">
                  <i class="bi bi-chevron-down"></i>
                  <select v-model="houseId" class="form-select" required>
                    <option value="">-- Select Property --</option>
                    <option v-for="h in houses" :key="h.id" :value="h.id">
                      {{ h.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="file-upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
                <input
                  type="file"
                  ref="fileInput"
                  class="d-none"
                  accept=".csv"
                  @change="handleFile"
                />
                <div v-if="!file" class="upload-placeholder">
                  <i class="bi bi-upload"></i>
                  <p><strong>Drop CSV file here</strong> or click to browse</p>
                  <p class="upload-hint">Supports .csv files only</p>
                </div>
                <div v-else class="file-preview">
                  <i class="bi bi-file-earmark-spreadsheet text-primary"></i>
                  <div class="file-info">
                    <strong>{{ file.name }}</strong>
                    <p>{{ formatFileSize(file.size) }}</p>
                  </div>
                  <button class="btn-remove" @click.stop="removeFile">
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>

              <button
                class="btn-upload"
                :disabled="loading || !houseId || !file"
                @click="upload"
              >
                <span v-if="loading">
                  <i class="bi bi-arrow-repeat spin"></i>
                  Processing...
                </span>
                <span v-else>
                  <i class="bi bi-cloud-upload"></i>
                  Upload Units
                </span>
              </button>
            </div>

            <!-- RESULTS -->
            <div v-if="result" class="results-card">
              <div class="result-header success">
                <i class="bi bi-check-circle-fill"></i>
                <h6>Upload Successful!</h6>
              </div>
              <div class="result-stats">
                <div class="stat">
                  <div class="stat-value">{{ result.inserted }}</div>
                  <div class="stat-label">Units Added</div>
                </div>
                <div class="stat">
                  <div class="stat-value">{{ result.rejected.length || 0 }}</div>
                  <div class="stat-label">Rejected</div>
                </div>
                <div class="stat">
                  <div class="stat-value">{{ result.house }}</div>
                  <div class="stat-label">Property</div>
                </div>
              </div>

              <div v-if="result.rejected && result.rejected.length" class="rejected-section">
                <div class="rejected-header">
                  <i class="bi bi-exclamation-triangle"></i>
                  <h6>Issues Found</h6>
                </div>
                <div class="rejected-list">
                  <div v-for="(r, i) in result.rejected" :key="i" class="rejected-item">
                    <div class="rejected-reason">{{ r.reason }}</div>
                    <div class="rejected-row">Row {{ r.row }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT SECTION - SINGLE UNIT FORM -->
      <div class="form-section">
        <div class="form-card">
          <div class="form-header">
            <div class="form-icon">
              <i class="bi bi-plus-circle-fill"></i>
            </div>
            <div>
              <h4 class="form-title">Add Single Unit</h4>
              <p class="form-subtitle">Add units one by one</p>
            </div>
          </div>

          <div class="form-divider">
            <span>Unit Information</span>
          </div>

          <form @submit.prevent="saveUnit">
            <!-- HOUSE SELECT -->
            <div class="form-group">
              <label class="form-label">
                <i class="bi bi-building"></i> Property
              </label>
              <div class="select-with-icon">
                <i class="bi bi-chevron-down"></i>
                <select v-model="unit.house_id" class="form-select" required>
                  <option value="">-- Select Property --</option>
                  <option v-for="h in houses" :key="h.id" :value="h.id">
                    {{ h.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- UNIT DETAILS -->
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bi bi-hash"></i> Unit Name/Number
                  </label>
                  <input v-model="unit.unit_number" class="form-control" 
                         placeholder="e.g., A101, Ground-01" required />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bi bi-house-door"></i> Unit Type
                  </label>
                  <select v-model="unit.unit_type" class="form-select">
                    <option value="">-- Select Type --</option>
                    <option>Single Room</option>
                    <option>One Bedroom</option>
                    <option>Two Bedroom</option>
                    <option>Three Bedroom</option>
                    <option>4 Bedroom</option>
                    <option>5 Bedroom</option>
                    <option>Office Space</option>
                    <option>SQ</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="form-divider">
              <span>Financial Details</span>
            </div>

            <!-- FINANCIAL INPUTS -->
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bi bi-cash-stack"></i> Rent (KES)
                  </label>
                  <div class="input-with-icon">
                    <span class="input-prefix">KES</span>
                    <input type="number" v-model="unit.rent_amount" 
                           class="form-control" placeholder="0" required />
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bi bi-shield-check"></i> Deposit (KES)
                  </label>
                  <div class="input-with-icon">
                    <span class="input-prefix">KES</span>
                    <input type="number" v-model="unit.deposit_amount" 
                           class="form-control" placeholder="0" required />
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bi bi-wrench"></i> Service Charge (KES)
                  </label>
                  <div class="input-with-icon">
                    <span class="input-prefix">KES</span>
                    <input type="number" v-model="unit.service_charge" 
                           class="form-control" placeholder="0" required />
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bi bi-plus-circle"></i> Other Charges (KES)
                  </label>
                  <div class="input-with-icon">
                    <span class="input-prefix">KES</span>
                    <input type="number" v-model="unit.other_charges" 
                           class="form-control" placeholder="0" />
                  </div>
                </div>
              </div>
            </div>

            <button class="btn-submit" type="submit">
              <i class="bi bi-check-circle"></i>
              Save Unit
            </button>

            <div class="form-hint">
              <i class="bi bi-info-circle"></i>
              This unit will be added immediately to the selected property
            </div>
          </form>
        </div>

        <!-- QUICK STATS -->
        <div class="stats-card">
          <h6><i class="bi bi-graph-up"></i> Quick Stats</h6>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-icon">
                <i class="bi bi-building"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ houses.length }}</div>
                <div class="stat-label">Properties</div>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">
                <i class="bi bi-house-door"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">Multiple</div>
                <div class="stat-label">Units Supported</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  data() {
    return {
      houses: [],
      houseId: "",
      file: null,
      loading: false,
      result: null,
      unit: {
        house_id: "",
        unit_number: "",
        unit_type: "",
        rent_amount: "",
        deposit_amount: "",
        service_charge: "",
        other_charges: ""
      }
    };
  },

  async mounted() {
    try {
      const res = await apiFetch("/houses");
      this.houses = res.data|| [];
    } catch (err) {
      console.error("House fetch error:", err);
    }
  },

  methods: {
    async saveUnit() {
      try {
        const response = await apiFetch("/units", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          
          body: JSON.stringify(this.unit),
        });

        
    if (response.success) {
      this.showToast("success", "Unit saved successfully!");
              this.$router.push("/houses");


    } else {
      throw new Error(response.message || "unit not added");
    }

        // if (!response.ok) {
        //   throw new Error("Failed to save unit");
        // }

        // this.showToast("success", "Unit saved successfully!");
        
        // Reset form
        this.unit = {
          house_id: "",
          unit_number: "",
          unit_type: "",
          rent_amount: 0,
          deposit_amount: 0,
          service_charge: 0,
          other_charges: 0
        };

      } catch (err) {
        this.showToast("error", "Error: " + err.message);
      }
    },

    handleFile(e) {
      this.file = e.target.files[0];
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    removeFile() {
      this.file = null;
      this.$refs.fileInput.value = '';
    },

    handleDrop(e) {
      const files = e.dataTransfer.files;
      if (files.length && files[0].name.endsWith('.csv')) {
        this.file = files[0];
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async downloadTemplate() {
      try {
        const response = await fetch(
          "https://golang.roberms.com/api/unit/download/template",
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        );

        if (!response.ok) {
          throw new Error("Template download failed");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "units_upload_template.csv";
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

      } catch (err) {
        console.error("Template download error:", err);
        this.showToast("error", "Failed to download template");
      }
    },

    async upload() {
  if (!this.houseId || !this.file) {
    this.showToast("warning", "Please select property and CSV file");
    return;
  }

  const formData = new FormData();
  formData.append("file", this.file);
  formData.append("house_id", this.houseId);

  try {
    this.loading = true;
    this.result = null;

    console.log("Uploading to: /units/upload-csv");
    console.log("House ID:", this.houseId);
    console.log("File:", this.file.name);

    const res = await apiFetch("/units/upload-csv", {
      method: "POST",
      body: formData,
      headers: {} // Empty headers for FormData
    });

    console.log("Response:", res);

    this.result = res;

    if (res && (res.success === true || res.inserted > 0)) {
      this.showToast("success", `Successfully added ${res.inserted || res.data?.units_added || 0} units`);
      
      // Reset form
      this.file = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    } else {
      this.showToast("error", res?.message || "Upload failed");
    }

  } catch (err) {
    console.error("Upload error details:", err);
    this.showToast("error", err.message || "Upload failed");
    this.result = {
      inserted: 0,
      rejected: [{ reason: err.message, row: 0 }],
      house: this.houses.find(h => h.id === this.houseId)?.name || "Unknown"
    };
  } finally {
    this.loading = false;
  }
},

    showToast(type, message) {
      // You can replace this with your preferred toast notification system
      alert(`${type.toUpperCase()}: ${message}`);
    }
  }
};
</script>

<style scoped>
.page-wrapper {
  background: var(--dash-bg);
  min-height: 100vh;
}

.add-unit-container {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 30px;
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;
}

/* LEFT SECTION STYLES */
.info-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.info-header {
  text-align: center;
  margin-bottom: 30px;
}

.icon-wrapper {
  width: 70px;
  height: 70px;
  background: var(--dash-primary-soft);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: var(--dash-primary-ink);
}

.icon-wrapper i {
  font-size: 1.75rem;
  color: var(--dash-primary-ink);
}

.title {
  font-size: var(--dash-fs-h1);
  font-weight: 700;
  color: var(--dash-ink);
  margin-bottom: 8px;
}

.subtitle {
  color: var(--dash-muted);
  font-size: 16px;
}

/* TEMPLATE CARD */
.template-card {
  background: var(--dash-primary-soft);
  border: 2px dashed var(--dash-primary-line);
  border-radius: 15px;
  padding: 25px;
  margin-bottom: 25px;
}

.template-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.template-header i {
  font-size: 24px;
  color: var(--dash-primary);
}

.template-header h5 {
  margin: 0;
  color: var(--dash-ink);
}

.template-desc {
  color: var(--dash-muted);
  margin-bottom: 20px;
}

.btn-template-download {
  width: 100%;
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 14px;
  border-radius: var(--dash-pill);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  margin-bottom: 20px;
}

.btn-template-download:hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.template-features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.feature {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--dash-text);
}

.feature i {
  color: var(--dash-success);
}

/* PROCESS STEPS */
.process-steps {
  margin: 20px 0;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--dash-line);
}

.step:last-child {
  border-bottom: none;
}

.step-number {
  width: 32px;
  height: 32px;
  background: var(--dash-line);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: var(--dash-text);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content p {
  color: var(--dash-muted);
  margin: 4px 0 0;
  font-size: 14px;
}

/* UPLOAD AREA */
.upload-card {
  background: white;
  border: 2px solid var(--dash-line);
  border-radius: 15px;
  padding: 25px;
  margin: 25px 0;
}

.upload-title {
  color: var(--dash-ink);
  margin-bottom: 20px;
  font-weight: 600;
}

.house-select-wrapper {
  margin-bottom: 20px;
}

.select-with-icon {
  position: relative;
}

.select-with-icon i {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dash-faint);
  pointer-events: none;
}

.file-upload-area {
  border: 2px dashed var(--dash-line-strong);
  border-radius: var(--dash-radius);
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
}

.file-upload-area:hover {
  border-color: var(--dash-primary);
  background: var(--dash-surface-2);
}

.upload-placeholder i {
  font-size: 2.25rem;
  color: var(--dash-faint);
  margin-bottom: 15px;
}

.upload-placeholder p {
  margin: 0;
  color: var(--dash-text);
}

.upload-hint {
  font-size: 14px;
  color: var(--dash-faint);
  margin-top: 5px !important;
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-sm);
}

.file-info {
  flex: 1;
  margin: 0 15px;
}

.file-info p {
  margin: 4px 0 0;
  color: var(--dash-muted);
  font-size: 14px;
}

.btn-remove {
  background: none;
  border: none;
  color: var(--dash-danger);
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
}

.btn-upload {
  width: 100%;
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 16px;
  border-radius: var(--dash-pill);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.btn-upload:disabled {
  background: var(--dash-line-strong);
  cursor: not-allowed;
  transform: none !important;
}

.btn-upload:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* RESULTS */
.results-card {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.result-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--dash-line);
}

.result-header.success {
  color: var(--dash-success);
}

.result-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}

.stat {
  text-align: center;
  padding: 15px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-sm);
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--dash-ink);
}

.stat-label {
  font-size: 14px;
  color: var(--dash-muted);
  margin-top: 5px;
}

.rejected-section {
  margin-top: 20px;
}

.rejected-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--dash-warning);
  margin-bottom: 15px;
}

.rejected-list {
  max-height: 200px;
  overflow-y: auto;
}

.rejected-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: var(--dash-warning-soft);
  border-radius: var(--dash-radius-xs);
  margin-bottom: 8px;
  border-left: 4px solid var(--dash-warning);
}

.rejected-reason {
  color: var(--dash-ink);
  font-size: 14px;
}

.rejected-row {
  color: var(--dash-muted);
  font-size: 12px;
  background: var(--dash-line);
  padding: 4px 8px;
  border-radius: var(--dash-radius-xs);
}

/* RIGHT SECTION STYLES */
.form-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  margin-bottom: 25px;
}

.form-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
}

.form-icon {
  width: 50px;
  height: 50px;
  background: var(--dash-warning-soft);
  border-radius: var(--dash-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-warning-ink);
}

.form-icon i {
  font-size: 24px;
  color: var(--dash-warning-ink);
}

.form-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0;
}

.form-subtitle {
  color: var(--dash-muted);
  font-size: 14px;
  margin: 4px 0 0;
}

.form-divider {
  position: relative;
  text-align: center;
  margin: 25px 0;
}

.form-divider span {
  background: white;
  padding: 0 15px;
  color: var(--dash-muted);
  font-size: 14px;
  font-weight: 600;
}

.form-divider:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--dash-line);
  z-index: -1;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--dash-text);
  margin-bottom: 8px;
}

.form-label i {
  color: var(--dash-muted);
}

.input-with-icon {
  position: relative;
}

.input-prefix {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dash-muted);
  font-weight: 500;
}

.input-with-icon .form-control {
  padding-left: 50px;
}

.form-control, .form-select {
  border: 2px solid var(--dash-line);
  border-radius: var(--dash-radius);
  padding: 12px 15px;
  transition: all 0.3s ease;
}

.form-control:focus, .form-select:focus {
  border-color: var(--dash-primary);
  box-shadow: var(--dash-shadow);
}

.btn-submit {
  width: 100%;
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 16px;
  border-radius: var(--dash-pill);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.btn-submit:hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.form-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-muted);
  font-size: 14px;
  margin-top: 20px;
  padding: 12px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-xs);
}

.form-hint i {
  color: var(--dash-primary);
}

/* STATS CARD */
.stats-card {
  background: var(--dash-primary);
  border-radius: var(--dash-radius-lg);
  padding: 25px;
  color: white;
}

.stats-card h6 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  font-weight: 600;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--dash-radius);
  backdrop-filter: blur(10px);
}

.stat-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--dash-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  font-size: 20px;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  opacity: 0.9;
}

/* RESPONSIVE DESIGN */
@media (max-width: 1200px) {
  .add-unit-container {
    grid-template-columns: 1fr;
    gap: 25px;
    padding: 20px;
  }
  
  .template-features {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .result-stats {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>